import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Typed from 'typed.js';
import { FaLinkedin, FaResearchgate, FaGraduationCap } from 'react-icons/fa';
import profilePic from '../assets/my-hero.webp';
import { publications } from './pubtimeline';

export default function Home() {
    const typedElement = useRef(null);

    useEffect(() => {
        const typed = new Typed(typedElement.current, {
            strings: [
                "I am an Associate Professor at<br/><span class=\"text-primary dark:text-blue-400\">Shiv Nadar University</span>."
            ],
            typeSpeed: 50,
            backSpeed: 30,
            loop: false,
            showCursor: false,
        });

        return () => {
            typed.destroy();
        };
    }, []);

    return (
        <header id="home" className="relative bg-background-light dark:bg-background-dark pt-8 pb-12 overflow-hidden w-full">
            <div className="absolute inset-0 bg-hex-pattern opacity-100 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background-light dark:to-background-dark pointer-events-none"></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">

                <motion.div
                    className="mb-4 relative inline-block"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-xl mx-auto">
                        <img
                            src={profilePic}
                            alt="Dr. Priyanka Katiyar Profile"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="absolute bottom-2 right-4 bg-primary text-white p-2 rounded-full shadow-lg flex items-center justify-center w-8 h-8">
                        <span className="material-icons text-sm">science</span>
                    </div>
                </motion.div>

                <motion.h1
                    className="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                >
                    <span ref={typedElement}></span>
                </motion.h1>

                <motion.p
                    className="text-lg text-gray-600 dark:text-gray-400 mb-6 font-light"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                >
                    Associate Professor | Researcher | Mentor
                </motion.p>

                <motion.p
                    className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-8 italic"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.8 }}
                >
                    Specializing in Biofuels, Membrane Separation, Supercritical Fluid Extraction, and Catalysis.
                </motion.p>

                <motion.div
                    className="flex justify-center items-center space-x-6 mb-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                >
                    <a href="https://www.linkedin.com/in/priyanka-k-3930a852/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-blue-400 transition-transform hover:-translate-y-1">
                        <FaLinkedin className="text-2xl" />
                    </a>
                    <a href="https://scholar.google.com/citations?hl=en&authuser=1&user=lLEBAwQAAAAJ" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-blue-400 transition-transform hover:-translate-y-1">
                        <FaGraduationCap className="text-2xl" />
                    </a>
                    <a href="https://www.researchgate.net/profile/Priyanka-Priyanka-3" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-blue-400 transition-transform hover:-translate-y-1">
                        <FaResearchgate className="text-2xl" />
                    </a>
                    <a href="/CV_Priyanka Katiyar.pdf" download className="inline-flex items-center px-6 py-2 bg-primary text-white rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-all ml-4">
                        Download CV <span className="material-icons text-sm ml-1">download</span>
                    </a>
                </motion.div>

                <motion.div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.8 }}
                >
                    <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="text-3xl font-bold text-primary dark:text-blue-400">{publications.length}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">Publications</div>
                    </div>
                    <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="text-3xl font-bold text-primary dark:text-blue-400">550+</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">Citations</div>
                    </div>
                    <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="text-3xl font-bold text-primary dark:text-blue-400">10</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">h-index</div>
                    </div>
                    <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="text-3xl font-bold text-primary dark:text-blue-400">₹1.81 Cr</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">Across 4 Grants</div>
                    </div>
                </motion.div>

            </div>
        </header>
    );
} 